/**
 * AI Sidebar Component
 *
 * A persistent sidebar for users who want the AI chat always visible.
 * Provides a Cursor-like experience with the AI assistant on the side.
 *
 * Thread state is managed by usePlasmaThread inside PlasmaAIChat.
 * This component only manages sidebar open/close state.
 */

import { cn } from "@plasma/utils/cn";
import { PanelRightClose, PanelRightOpen } from "lucide-react";

import { Button } from "~/components/ui/button";
import { logger, PortalEvents } from "~/lib/logger";

import { PlasmaAIChat } from "./plasma-ai-chat";

interface AISidebarProps {
  /** Whether the sidebar is currently open */
  isOpen: boolean;
  /** Called when the sidebar open state changes */
  onOpenChange: (open: boolean) => void;
  /** Optional class name */
  className?: string;
}

export function AISidebar({ isOpen, onOpenChange, className }: AISidebarProps) {
  const handleOpenChange = (open: boolean) => {
    if (open) {
      logger.info(PortalEvents.AI_SIDEBAR_OPENED);
    } else {
      logger.info(PortalEvents.AI_SIDEBAR_CLOSED);
    }
    onOpenChange(open);
  };

  return (
    <>
      {/* Toggle button (visible when closed) */}
      {!isOpen && (
        <Button
          aria-label="Open Plasma AI"
          className={cn(
            "fixed top-1/2 right-4 z-40 -translate-y-1/2",
            "h-10 w-10 rounded-full shadow-md",
            "bg-background/95 supports-[backdrop-filter]:bg-background/60 backdrop-blur"
          )}
          onClick={() => handleOpenChange(true)}
          size="icon"
          type="button"
          variant="outline"
        >
          <PanelRightOpen className="h-5 w-5" />
        </Button>
      )}

      {/* Sidebar panel - fixed on right edge, slides in/out */}
      <div
        className={cn(
          "fixed top-0 right-0 z-40 h-dvh w-full sm:max-w-[380px]",
          "bg-background border-l",
          "shadow-xl",
          "transition-transform duration-200 ease-out",
          isOpen ? "translate-x-0" : "translate-x-full",
          className
        )}
      >
        {/* Header with close button */}
        <div className="flex items-center justify-between border-b p-3">
          <span className="text-sm font-semibold">Plasma AI</span>
          <Button
            aria-label="Close sidebar"
            className="h-8 w-8"
            onClick={() => handleOpenChange(false)}
            size="icon"
            type="button"
            variant="ghost"
          >
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>

        {/* Chat component manages its own thread state */}
        <div className="h-[calc(100%-57px)]">
          <PlasmaAIChat onClose={() => handleOpenChange(false)} />
        </div>
      </div>

      {/* Overlay when sidebar is open on smaller screens */}
      {isOpen && (
        <button
          aria-label="Close sidebar"
          className="bg-foreground/20 fixed inset-0 z-30 lg:hidden"
          onClick={() => handleOpenChange(false)}
          type="button"
        />
      )}
    </>
  );
}

// =============================================================================
// Sidebar Container with Main Content
// =============================================================================

interface AISidebarLayoutProps {
  /** Main content to render */
  children: React.ReactNode;
  /** Whether the sidebar is open */
  isSidebarOpen: boolean;
  /** Called when sidebar state changes */
  onSidebarOpenChange: (open: boolean) => void;
}

/**
 * Layout component that adjusts main content when AI sidebar is open.
 * Provides smooth content adjustment for the "Cursor effect".
 */
export function AISidebarLayout({
  children,
  isSidebarOpen,
  onSidebarOpenChange,
}: AISidebarLayoutProps) {
  return (
    <div className="relative min-h-dvh">
      {/* Main content with margin adjustment */}
      <div
        className={cn(
          "transition-[margin-right] duration-300 ease-in-out",
          isSidebarOpen && "lg:mr-[380px]"
        )}
      >
        {children}
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isSidebarOpen} onOpenChange={onSidebarOpenChange} />
    </div>
  );
}
